/*
 * WebCRD
 * Web to print solution that automates ordering, fulfillment, job ticketing, production management and chargebacks across corporate print centers.
 * Copyright 1999-2026 Rochester Software Associates (service@rocsoft.com)
 */

import PropTypes from 'prop-types';
import { useState } from 'react';
import { DropdownToggle, UncontrolledDropdown } from 'reactstrap';

import { automationIDPropTypes, getExtraAutomationID } from '~/components/id/AutomationID';
import PlaceholderText from '~/components/skeleton/PlaceholderText';
import Tooltip from '~/components/tooltip/Tooltip';
import useCallbackRef from '~/utils/hooks/useCallbackRef';
import localizedStringOrNode, { localizedStringOrNodePropTypes } from '~components/text/LocalizedStringOrNode';

import FormHelp from '../FormHelp';
import RequiredFieldIndicator from '../RequiredFieldIndicator';

import DropdownMenu from './DropdownMenu';
import getSelectedOption from './getSelectedOption';

const DropdownLabel = ({ labelID, label, required }) => {
    // Why the inner span? Because some screen readers (at least Ubuntu's built-in one) will read the label twice if it isn't
    // Why not use a span instead of a label on the outer node? Because the bootstrap css for form-floating expects a label element
    return (
        <label>
            <span id={labelID}>{localizedStringOrNode(label)}</span>
            {required && <RequiredFieldIndicator />}
        </label>
    );
};

DropdownLabel.propTypes = {
    label: localizedStringOrNodePropTypes.isRequired,
    labelID: PropTypes.string.isRequired,
    required: PropTypes.bool,
};
const Dropdown = ({ automationID, label, help, value, setValue, options, required, labelOnLeft, type }) => {
    // In order for the current option to display correctly if it's invalid,
    // the parent component needs to pass a full dropdown option as the value,
    // instead of just the underlying value.
    // However, if this is done when the current option IS valid,
    // then the parent component won't have the correct value,
    // since we call setValue with the underlying value in DropdownMenu,
    // and many callers have filters that rely on having the underlying value.
    // We don't want every caller to have to worry about this distinction,
    // so fix it here by saving initial value that we are given if it's a full option,
    // and then call setValue with the underlying value, so that the caller's logic works correctly.
    // If we get the same value multiple times in a row, assume that the caller just passes in options instead of values
    const [savedOption, setSavedOption] = useState(null);

    if (value !== null && typeof value === 'object' && Object.prototype.hasOwnProperty.call(value, 'value') && value !== savedOption) {
        if (value.value !== savedOption?.value || value.label !== savedOption?.label) {

            // Set the value of dropdown ON LOAD (this is not when the user actually selects something with their mouse)
            if (value.value) {
                // If the value is blank, don't initialze the dropdown selected value with a blank
                // Because sometimes the setValue is also calling the updateCurrentUser and changing the address
                // And with the location dropdown, we don't want to set the location to blank if the address hasn't been
                // full loaded yet and thus the value is blank (as we do in useValidValuesAsDropdownOptions)

                // If they truly wanted to choose blank, we don't need to initialize a blank because an unselected dropdown will be the first option (the blank)
                // And then if they want to actually set it to blank, the setValue on the DropdownMenu down below WILL allow a blank (we won't have this empty check)
                setValue(value.value);
            }
            setSavedOption(value);
        }
    }

    // Use the saved option if the value is the same
    // This allows invalid options to have the correct display label
    const valueToUse = savedOption?.value === value ? savedOption : value;
    const selectedOption = getSelectedOption(valueToUse, options);

    // TODO JAS webcrd_dev-34 Improve support for mobiles by making the selector full-screen?
    const { id: toggleContentID } = automationID;
    const { id: buttonID } = getExtraAutomationID(automationID, 'dropdown');
    const { id: labelID } = getExtraAutomationID(automationID, 'label');
    const helpID = help && getExtraAutomationID(automationID, 'help');

    const containerClasses = [];
    if (labelOnLeft) {
        containerClasses.push('form-label-on-left');
    } else {
        containerClasses.push('form-floating');
    }

    const labelElement = label && <DropdownLabel label={label} labelID={labelID} required={required}/>;

    const dropdownClasses = ['form-control'];
    if (selectedOption.invalid || required && !selectedOption.value) {
        dropdownClasses.push('is-invalid');
    }

    const [tooltipRef, setTooltipRef] = useCallbackRef();

    let labelDisplay = selectedOption.label;

    if (!options || options.length === 0) {
        // If there are no options, display a "loading" placeholder
        labelDisplay = <PlaceholderText placeholderFor="dropdown options"/>;
    }

    return (
        <div className={containerClasses.join(' ')}>
            {labelOnLeft && labelElement}
            <UncontrolledDropdown
                className="stylized-select"
                menuRole="listbox"
                data-required={required ? true : undefined}
                data-invalid={selectedOption.invalid ? true : undefined}
            >
                {selectedOption.label && <Tooltip target={tooltipRef}>{selectedOption.label}</Tooltip>}
                <DropdownToggle
                    id={buttonID}
                    caret
                    tag="button"
                    className={dropdownClasses.join(' ')}
                    aria-describedby={`${labelID || ''}`}
                    aria-labelledby={`${labelID || ''}`}
                    disabled={!setValue}
                    data-value={selectedOption.ariaLabel}
                    innerRef={setTooltipRef}
                    type={type}
                >
                    <div aria-label={selectedOption.ariaLabel} id={toggleContentID}>{labelDisplay}</div>
                </DropdownToggle>
                {options && <DropdownMenu labelID={labelID} options={options} value={selectedOption.value} setValue={setValue} />}
            </UncontrolledDropdown>
            {!labelOnLeft && labelElement}
            <FormHelp helpID={helpID && helpID.id} help={help} />
        </div>
    );
};

Dropdown.propTypes = {
    automationID: automationIDPropTypes,
    label: localizedStringOrNodePropTypes.isRequired,
    help: localizedStringOrNodePropTypes,
    value: PropTypes.any,
    selectionLabel: PropTypes.node,
    setValue: PropTypes.func,
    required: PropTypes.bool,
    options: PropTypes.oneOfType([
        PropTypes.arrayOf(
            PropTypes.shape({
                value: PropTypes.any,
                label: PropTypes.node,
            })
        ),
        PropTypes.arrayOf(PropTypes.string),
    ]).isRequired,
    labelOnLeft: PropTypes.bool,
    type: PropTypes.string,
};

export default Dropdown;
